import { useEffect, useId, useRef, useState } from "react";
import moment from "moment";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { Button, LGBDatepicker } from "@components";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useUserSession } from "../../context/UserContext";
import { useDataCache } from "../../data/useDataCache";
import {
  getInvoiceCandidates,
  reviewConsolidatedInvoice,
  createConsolidatedInvoice,
  InvoiceCandidate,
  InvoiceReview,
} from "../../api/consolidatedInvoices";
import { queryKeys } from "../../data/queryKeys";
import { WorkspaceRole } from "../../types";
import { InvoiceLines } from "../InvoiceDetails/InvoiceLines";

/** Both entry points use this bounded queue/review/create flow. */
export const ReadyToInvoice = ({ customerId }: { customerId?: string }) => {
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const { t, i18n } = useTranslation("invoice");
  const workspace = activeWorkspace?.workspaceId || "";
  const token = userSession?.token.accessToken || "";
  const allowed =
    activeWorkspace?.role === WorkspaceRole.Owner ||
    activeWorkspace?.role === WorkspaceRole.Administrator;
  const client = useQueryClient();
  const { invalidateInvoices, invalidateOrders } = useDataCache();
  const queue = useInfiniteQuery(
    queryKeys.invoiceCandidates(workspace, customerId),
    ({ pageParam }) =>
      getInvoiceCandidates(token, workspace, customerId, pageParam),
    {
      enabled: allowed && !!workspace && !!token,
      getNextPageParam: (page) => page.nextCursor || undefined,
      retry: false,
    },
  );
  const [group, setGroup] = useState<InvoiceCandidate[] | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [review, setReview] = useState<InvoiceReview>();
  const [result, setResult] = useState<string>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const filterId = useId();
  const requestId = useRef(uuid());
  const inFlight = useRef(false);
  const scope = useRef(0);
  useEffect(() => {
    setGroup(null);
    setReview(undefined);
    setResult(undefined);
    setError("");
    setBusy(false);
    return () => {
      scope.current += 1;
    };
  }, [workspace, customerId]);
  const money = (n: number) =>
    new Intl.NumberFormat(i18n.language === "no" ? "nb-NO" : "en-GB", {
      style: "currency",
      currency: "NOK",
    }).format(n);
  const date = (day: string) => day.split("-").reverse().join(".");
  const errorText = (failure: unknown, creating: boolean) => {
    const response = (failure as { response?: { data?: { error?: string } } })
      ?.response;
    const code = response?.data?.error;
    const keys: Record<string, string> = {
      ORDER_ALREADY_INVOICED: "already_invoiced",
      ORDER_INVOICE_CHANGED: "changed",
      CONSOLIDATED_INCOMPATIBLE: "incompatible",
      CONSOLIDATED_INVALID_SELECTION: "limit",
      CONSOLIDATED_TOO_LARGE: "limit",
      ORDER_INVOICE_INVALID_PRICING: "pricing",
      ORDER_INVOICE_ZERO_TOTAL: "pricing",
      ORDER_INVOICE_MISSING_TIME: "pricing",
      DRIVER_ORDER_REVIEW_REQUIRED: "driver_review_required",
    };
    return t(
      `consolidated.${code && keys[code] ? keys[code] : creating ? "failed" : "review_failed"}`,
    );
  };
  const perform = async (creating: boolean) => {
    if (inFlight.current || !selected.length || (creating && !review)) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    const run = scope.current;
    try {
      if (creating) {
        const created = await createConsolidatedInvoice(
          token,
          workspace,
          selected,
          review!.reviewToken,
          requestId.current,
        );
        if (!created.invoice?.id) throw new Error("Missing invoice");
        if (run === scope.current) {
          setResult(created.invoice.id);
          invalidateInvoices();
          invalidateOrders();
          client.invalidateQueries(queryKeys.invoiceCandidates(workspace));
        }
      } else {
        const value = await reviewConsolidatedInvoice(
          token,
          workspace,
          selected,
        );
        if (run === scope.current) {
          setReview(value);
          requestId.current = uuid();
        }
      }
    } catch (failure) {
      if (run === scope.current) setError(errorText(failure, creating));
    } finally {
      inFlight.current = false;
      if (run === scope.current) setBusy(false);
    }
  };
  const candidateError = (code?: string) => {
    if (code === "DRIVER_ORDER_REVIEW_REQUIRED")
      return t("consolidated.driver_review_required");
    if (code === "ORDER_ALREADY_INVOICED")
      return t("consolidated.already_invoiced");
    return t("consolidated.settings");
  };
  const orders = Array.from(
    new Map(
      (queue.data?.pages.flatMap((page) => page.orders) || []).map((order) => [
        order.orderId,
        order,
      ]),
    ).values(),
  );
  const groups = new Map<string, InvoiceCandidate[]>();
  for (const order of orders) {
    if ((from && order.completedAt < from) || (to && order.completedAt > to))
      continue;
    const key = JSON.stringify([order.customerId, order.companyId]);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(order);
  }
  for (const items of Array.from(groups.values())) {
    items.sort(
      (left, right) =>
        left.completedAt.localeCompare(right.completedAt) ||
        left.orderNumber.localeCompare(right.orderNumber, undefined, {
          numeric: true,
        }),
    );
  }
  if (!allowed) return null;
  if (result)
    return (
      <div className="space-y-3">
        <p role="status">{t("consolidated.created")}</p>
        <Link
          className="text-brand underline"
          to={`/invoices?id=${encodeURIComponent(result)}`}
        >
          {t("consolidated.open")}
        </Link>
      </div>
    );
  return (
    <section className="space-y-4">
      {error && (
        <p role="alert" className="text-red-600">
          {error}
        </p>
      )}
      {group ? (
        <>
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold">
              {group[0].customerName} · {group[0].companyName}
            </h2>
            <button
              disabled={busy}
              className="text-brand disabled:opacity-50"
              onClick={() => {
                setGroup(null);
                setReview(undefined);
                setError("");
              }}
            >
              {t("consolidated.back")}
            </button>
          </div>
          <p className="text-sm text-secondary">
            {t("consolidated.selection_hint")}
          </p>
          <fieldset
            disabled={busy || !!review}
            className="divide-y divide-border"
          >
            {group.map((order) => (
              <label
                key={order.orderId}
                className="flex cursor-pointer items-center gap-3 py-3"
              >
                <input
                  type="checkbox"
                  className="text-brand focus:ring-brand"
                  checked={selected.includes(order.orderId)}
                  disabled={
                    !!order.error ||
                    (!selected.includes(order.orderId) && selected.length >= 50)
                  }
                  onChange={() =>
                    setSelected((ids) =>
                      ids.includes(order.orderId)
                        ? ids.filter((id) => id !== order.orderId)
                        : [...ids, order.orderId],
                    )
                  }
                />
                <span>
                  #{order.orderNumber} · {date(order.completedAt)}
                </span>
                <Link
                  className="ml-auto text-brand underline"
                  to={`/orders?id=${encodeURIComponent(order.orderId)}`}
                >
                  {t("consolidated.order")}
                </Link>
                {order.error && (
                  <span className="text-red-600">
                    {candidateError(order.error)}
                  </span>
                )}
              </label>
            ))}
          </fieldset>
          {review ? (
            <>
              <div className="rounded border border-border p-3 space-y-2">
                <InvoiceLines invoice={review.invoice} />
                <p className="font-semibold">
                  {t("consolidated.total")}:{" "}
                  {money(review.invoice.amounts?.totalAmount || 0)}
                </p>
              </div>
              <p className="text-sm text-secondary">
                {t("consolidated.draft_hint")}
              </p>
              <div className="flex gap-3">
                <Button disabled={busy} onClick={() => perform(true)}>
                  {t("consolidated.create")}
                </Button>
                <button
                  disabled={busy}
                  className="text-brand"
                  onClick={() => {
                    setReview(undefined);
                    setError("");
                  }}
                >
                  {t("consolidated.edit_selection")}
                </button>
              </div>
            </>
          ) : (
            <Button
              disabled={busy || !selected.length}
              onClick={() => perform(false)}
            >
              {t("consolidated.review", { count: selected.length })}
            </Button>
          )}
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            {(
              [
                ["from", from, setFrom],
                ["to", to, setTo],
              ] as const
            ).map(([name, value, update]) => (
              <div key={name} className="flex items-center gap-2">
                <label htmlFor={`${filterId}-${name}`} className="text-sm">
                  {t(`consolidated.${name}`)}
                </label>
                <div className="w-44">
                  <LGBDatepicker
                    key={value ? "selected" : "empty"}
                    inputId={`${filterId}-${name}`}
                    selectedDate={
                      value ? moment(value, "YYYY-MM-DD").toDate() : undefined
                    }
                    select={(day) => update(moment(day).format("YYYY-MM-DD"))}
                    allowPastDates
                    maxDate={null}
                    overlayCalendar
                  />
                </div>
              </div>
            ))}
            {(from || to) && (
              <button
                className="text-sm text-brand"
                onClick={() => {
                  setFrom("");
                  setTo("");
                }}
              >
                {t("consolidated.clear_dates")}
              </button>
            )}
          </div>
          {queue.isLoading && <p>{t("consolidated.loading")}</p>}
          {queue.isError && (
            <div role="alert">
              <p>{t("consolidated.load_failed")}</p>
              <button className="text-brand" onClick={() => queue.refetch()}>
                {t("consolidated.retry")}
              </button>
            </div>
          )}
          {!queue.isLoading && !queue.isError && !groups.size && (
            <p>{t("consolidated.empty")}</p>
          )}
          <div className="divide-y divide-border">
            {Array.from(groups.entries()).map(([key, items]) => (
              <div
                key={key}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <div>
                  <p className="font-medium">
                    {items[0].customerName ||
                      t("consolidated.unknown_customer")}
                  </p>
                  <p className="text-sm text-secondary">
                    {items[0].companyName || t("consolidated.settings")} ·{" "}
                    {t("consolidated.orders", { count: items.length })}
                  </p>
                  {items.some((item) => item.error) && (
                    <p className="text-sm text-red-600">
                      {candidateError(
                        items.find((item) => item.error)?.error,
                      )}
                    </p>
                  )}
                </div>
                <Button
                  disabled={!items.some((item) => !item.error)}
                  onClick={() => {
                    setGroup(items);
                    setSelected(
                      items
                        .filter((item) => !item.error)
                        .slice(0, 50)
                        .map((item) => item.orderId),
                    );
                  }}
                >
                  {t(
                    items.length > 1
                      ? "consolidated.title"
                      : "consolidated.single",
                  )}
                </Button>
              </div>
            ))}
          </div>
          {queue.hasNextPage && (
            <div>
              <p className="text-sm text-secondary">
                {t("consolidated.more_hint")}
              </p>
              <button
                className="text-brand"
                disabled={queue.isFetchingNextPage}
                onClick={() => queue.fetchNextPage()}
              >
                {t("consolidated.more")}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};
